<script>
	import Grid from '$lib/Components/Layout/Grid/Grid.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import ReadMoreIndowStories from '$lib/PageSections/ReadMoreIndowStories/ReadMoreIndowStories.svelte';

	import closeupWindowIndowWindowReplacementAlternative from './images/closeupWindowIndowWindowReplacementAlternative.jpg';
	import exteriorViewHomeIndowInsertsWindowReplacementAlternative from './images/exteriorViewHomeIndowInsertsWindowReplacementAlternative.jpg';
	import familyEatingNextWindowIndowInsertsWindowReplacementAlternative from './images/familyEatingNextWindowIndowInsertsWindowReplacementAlternative.png';
	import singlePaneNoiseReduction50 from './images/single-pane-noise-reduction-50.png';
	import singlePaneNoiseReduction70 from './images/single-pane-noise-reduction-70.png';
</script>

<Section>
	<p>
		Craig and Shari loved their Portland home with its awesome neighbors and
		close proximity to schools and restaurants. When their growing family
		required larger quarters, they decided to add a story to their home rather
		than move. Their budget limited the family to vinyl windows if they wanted
		thermal replacement windows throughout the home. They started looking for a
		window replacement alternative that would keep the cold out.
	</p>
	<Image
		alt="View of home with Indow inserts. Save your old windows & avoid thermal window replacement projects."
		src={exteriorViewHomeIndowInsertsWindowReplacementAlternative}
	/>
	<p>
		They wanted to stop the energy loss and high heating bills, but the look of
		vinyl would not fit with the charm of their 1922 home. Windows account for
		nearly one third of energy loss in a home, so they needed to find a thermal
		window replacement alternative that would allow them to preserve their
		windows while still reducing their energy bills by up to 20%. Their
		solution: high-end, double-pane wood windows for the second floor addition
		and Indow window inserts throughout the first floor.
	</p>
	<p>
		Indow inserts are AERC certified. AERC, the <a
			href="https://aercenergyrating.org/product-search/residential-product-search"
			target="_blank"
			rel="external">Attachments Energy Rating Council</a
		>, is an unaffiliated organization that rates window attachment products on
		<a href="/performance">energy efficiency</a> by measuring various factors such
		as Air Leakage (AL) and U-Factor.
	</p>
	<p>
		On air leakage, Indow scored in the top two out of 33 storm windows tested,
		11 of which were interior storm windows. U-Factor is an indicator of heat
		transfer and tells you how well a window product insulates. Indow inserts
		have a U-Factor of .3, making them a top performer on insulation among all
		available storm windows. Installing Indow inserts in original windows, like
		Craig and Shari’s, allow them to perform at the level of thermal pane
		windows.
	</p>
	<Image
		alt="closeup of window with insert, thermal window replacement alternative that will keep warm for less."
		src={closeupWindowIndowWindowReplacementAlternative}
	/>
</Section>

<Section backgroundColor="grey-light">
	<h2>Testimonial</h2>
	<Image
		alt="Family dinner next to window with Indow insert, an alternative to thermal pane window replacements."
		src={familyEatingNextWindowIndowInsertsWindowReplacementAlternative}
	/>
	<div class="grid justify-items-center gap-y-2">
		<p class="text-lg text-indow-blue! italic">
			“The old double-hung windows make the space feel much more relaxed and
			comfortable than if I’d gotten window replacements, but my tiny electric
			space heater would have been going 24/7. Using Indow inserts, the space
			heats up fast and it stays warm for hours, even after I switch off the
			space heater. It’s actually more comfortable to sit on the sofa next to an
			Indow insert than it is to sit next to the brand new double panes. We’ve
			been pleasantly surprised by the Indow inserts!”
		</p>
		<p>– Craig & Shari Strong, Home Owners, Portland, Oregon.</p>
	</div>
	<a href="/resources/case-studies" class="italic">Read more case studies.</a>
</Section>

<Section>
	<Grid columns={2} rows={2} center class="gap-10!">
		<Grid subgrid vertical>
			<h3 class="grid text-2xl font-bold text-indow-grey! uppercase">
				<span>ACOUSTIC</span>
				<span>INDOW INSERT</span>
				<span>+</span>
				<span>SINGLE-PANE</span>
			</h3>
			<Image
				src={singlePaneNoiseReduction70}
				alt="Diagram of Acoustic window inserts with single-pane windows. Reduce noise by 70%."
				class="w-52"
			/>
		</Grid>
		<Grid subgrid vertical>
			<h3 class="grid text-2xl font-bold text-indow-grey! uppercase">
				<span>ACOUSTIC</span>
				<span>INDOW INSERT</span>
				<span>+</span>
				<span>DOUBLE-PANE</span>
			</h3>
			<Image
				src={singlePaneNoiseReduction50}
				alt="Diagram of Acoustic window inserts with double-pane windows. Reduce noise by up to 50%."
				class="w-52"
			/>
		</Grid>
	</Grid>
	<p>
		The condensation that used to steam up the inside of their single-panes has
		disappeared and the Strongs noticed another benefit, the first floor is now
		very quiet. They love how walker-friendly the community is, but it’s nice
		not to hear every conversation as their neighbors stroll by.
	</p>
	<p>
		When Craig converted the detached garage in their backyard into a studio, he
		fitted two beautiful recycled single-pane windows and fitted them with Indow
		inserts. Stories like the Strongs’ warm our hearts. Tell us yours!
	</p>
	<div class="mt-16 w-full border-b border-indow-blue"></div>
</Section>

<ReadMoreIndowStories />
<GetAFreeEstimate />
